/**
 * Profit-center "create order for a contractor" page.
 *
 * Always bound: the scope provider defaults to the user's sticky last
 * contractor (or the PC's first contractor), so this page is just the
 * standard NewOrderFlow keyed by contractor org — switching contractors via
 * the banner picker remounts it cleanly against that contractor's scope
 * (header + isolated query cache). There is no unbound browse state; the only
 * special case is a PC with no contractors yet, which points to the
 * contractors page.
 */

import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { Building2 } from "lucide-react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { NewOrderFlow } from "@/components/orders/NewOrderFlow";
import { usePcOrderScope } from "@/components/pc/PcOrderScopeProvider";
import { ContractorPicker } from "@/components/pc/ContractorPicker";
import { usePcHomeRoute } from "@/hooks/use-will-call";

function PageFrame({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <Layout
      headerContent={
        <span className="font-semibold">{t("pages.pcNewOrder.title")}</span>
      }
    >
      {children}
    </Layout>
  );
}

export default function PcNewOrder() {
  const { t } = useTranslation();
  const scope = usePcOrderScope();
  const [, setLocation] = useLocation();
  const pcHomeRoute = usePcHomeRoute();

  // Scope not resolved yet: org metadata confirming this is a PC org, the
  // contractor list loading, or the initial contractor being bound — all a
  // render or two on a fresh page load.
  if (!scope || !scope.restored) {
    return (
      <PageFrame>
        {scope?.contractorsError ? (
          <p className="text-destructive py-10 text-center">
            {t("pages.pcNewOrder.contractorsLoadFailed")}
          </p>
        ) : (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="h-8 w-48 bg-muted rounded animate-pulse" />
          </div>
        )}
      </PageFrame>
    );
  }

  // The provider defaults to the first contractor, so no contractor bound
  // means the PC has none yet.
  if (!scope.contractor) {
    return (
      <PageFrame>
        <div className="text-center py-16 space-y-3">
          <Building2 className="h-10 w-10 text-muted-foreground mx-auto" />
          <p className="font-medium">{t("pages.pcNewOrder.noContractorsTitle")}</p>
          <p className="text-muted-foreground text-sm">
            {t("pages.pcNewOrder.noContractorsDescription")}
          </p>
          <Button onClick={() => setLocation("/pc/contractors")}>
            {t("pages.pcNewOrder.goToContractors")}
          </Button>
        </div>
      </PageFrame>
    );
  }

  return (
    <NewOrderFlow
      key={scope.contractor.orgId}
      onBack={() => setLocation(pcHomeRoute)}
      // PCs have no page listing draft orders (the will-call dashboard shows
      // submitted orders only), and an explicit draft would surface in the
      // CONTRACTOR's orders list unannounced. The persistent cart already
      // preserves in-progress work, so drafts add nothing here.
      hideSaveDraft
      contextBanner={
        <div className="flex items-center gap-2 px-3 py-2 rounded-md border bg-muted/50 text-sm lg:w-fit lg:max-w-full">
          <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="text-muted-foreground flex-shrink-0">
            {t("pages.pcNewOrder.orderingFor")}
          </span>
          <ContractorPicker
            compact
            contractors={scope.contractors}
            value={scope.contractor}
            onSelect={scope.selectContractor}
            isLoading={scope.contractorsLoading}
          />
        </div>
      }
    />
  );
}
